/**
 * One assigned member: what needs doing for them, then their wardrobe,
 * outfits, profile and history, all from the assignment-scoped member read.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, RefreshCw01 } from '@untitledui/icons';
import { useParams, useSearchParams } from 'react-router';
import type { MemberDetail, MemberGarmentSummary, MemberOutfitSummary } from '@closet-theory/api';
import { Tabs } from '@ui/components/application/tabs/tabs';
import { Button } from '@ui/components/base/buttons/button';
import { SelectField, TextField } from '@ui/console/forms';
import { ActionDialog } from '@ui/console/PageShell';
import { Callout, FailureText, MetaItem, Section } from '@ui/console/primitives';
import { ResourceState } from './components';
import { GarmentImage } from './GarmentImage';
import {
  NOT_PROVIDED,
  accountStatus,
  garmentAttributes,
  garmentName,
  garmentSearchText,
  garmentStatus,
  humanize,
  outfitSource,
  relativeTime,
  shortDate,
} from './member-format';
import { ProfileGroup, profileGroups, profileUpdated, textOf } from './member-profile';
import {
  Card,
  DetailList,
  GARMENT_GRID_CLASS,
  GarmentThumbs,
  GarmentTile,
  MemberAvatar,
  categoriesOf,
  toReviewGarment,
} from './member-ui';
import { memberEmail, memberService, type MemberService } from './member-service';
import {
  reviewService,
  reviewStatusLabel,
  reviewerLabel,
  type QueuedOutfit,
  type ReviewService,
} from './review-service';

export const MEMBER_TABS = [
  { id: 'attention', label: 'Needs attention' },
  { id: 'wardrobe', label: 'Wardrobe' },
  { id: 'outfits', label: 'Outfits' },
  { id: 'profile', label: 'Profile' },
  { id: 'activity', label: 'Activity' },
] as const;
type TabId = (typeof MEMBER_TABS)[number]['id'];

function resolveTab(value: string | null): TabId {
  return MEMBER_TABS.some((tab) => tab.id === value) ? (value as TabId) : 'attention';
}

export function memberName(detail: MemberDetail): string {
  for (const key of ['name', 'full_name', 'display_name']) {
    const value = detail.user[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return 'This member';
}

/** The member read's lists that page by offset. */
type PagedList = 'garments' | 'outfits';

type MorePages = {
  garments: MemberGarmentSummary[];
  outfits: MemberOutfitSummary[];
};

const NO_MORE: MorePages = { garments: [], outfits: [] };

function uniqueById<T extends { id: string }>(items: T[]): T[] {
  const seen = new Set<string>();
  return items.filter((item) => (seen.has(item.id) ? false : (seen.add(item.id), true)));
}

/* ------------------------------------------------------------ needs attention */

function TomorrowOutfit({
  outfit,
  name,
}: {
  outfit: QueuedOutfit | undefined | null;
  name: string;
}) {
  if (outfit === undefined) {
    return <p className="text-sm text-tertiary">Checking tomorrow's outfit…</p>;
  }
  if (outfit === null) {
    return (
      <p className="text-sm text-tertiary">No outfit is waiting for review for {name} tomorrow.</p>
    );
  }
  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-semibold text-primary">
            Outfit for {shortDate(outfit.forDate)}
          </span>
          <span className="text-xs text-tertiary">Awaiting your review</span>
        </div>
        <Button
          size="sm"
          href={`/review/${encodeURIComponent(outfit.slotId)}?q=${encodeURIComponent(name)}`}
        >
          Review tomorrow's outfit
        </Button>
      </div>
      <GarmentThumbs garments={outfit.garments} label={`${name}'s outfit for tomorrow`} />
    </Card>
  );
}

/* ------------------------------------------------------------------ wardrobe */

function GarmentDialog({
  garment,
  onClose,
}: {
  garment: MemberGarmentSummary;
  onClose: () => void;
}) {
  const status = garmentStatus(garment.status);
  const attributes = garmentAttributes(garment.attributes);
  return (
    <ActionDialog
      title={garmentName(garment)}
      width="lg"
      onClose={onClose}
      footer={
        <Button size="sm" color="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-[minmax(0,16rem)_minmax(0,1fr)]">
        <GarmentImage garment={toReviewGarment(garment)} size="tile" />
        <div className="flex flex-col gap-4">
          <DetailList
            items={[
              ['Category', humanize(garment.category) || 'Not recorded'],
              ['Added', relativeTime(garment.created_at)],
              ['Ready to style', status ? status : 'Yes'],
              ...attributes,
            ]}
          />
          {attributes.length === 0 ? (
            <span className="text-xs text-tertiary">
              No colour, fabric or other details have been recorded for this garment yet.
            </span>
          ) : null}
        </div>
      </div>
    </ActionDialog>
  );
}

function Wardrobe({
  garments,
  total,
  onLoadMore,
  loadingMore,
}: {
  garments: MemberGarmentSummary[];
  total: number;
  onLoadMore: (() => void) | undefined;
  loadingMore: boolean;
}) {
  const [category, setCategory] = useState('');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState<MemberGarmentSummary>();
  const categories = useMemo(() => categoriesOf(garments), [garments]);
  const needle = query.trim().toLowerCase();
  const shown = garments.filter(
    (garment) =>
      (!category || garment.category === category) &&
      (!needle || garmentSearchText(garment).includes(needle)),
  );
  const partial = garments.length < total;
  return (
    <Section title="Wardrobe" description={`${total} garment${total === 1 ? '' : 's'} in total.`}>
      <div className="grid max-w-2xl grid-cols-1 gap-3 sm:grid-cols-2">
        <TextField
          label="Search this wardrobe"
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <SelectField
          label="Show category"
          value={category}
          onChange={(event) => setCategory(event.target.value)}
          options={[
            { value: '', label: 'All categories' },
            ...categories.map((value) => ({
              value,
              label: `${humanize(value)} (${garments.filter((g) => g.category === value).length})`,
            })),
          ]}
        />
      </div>
      {partial ? (
        <p className="text-xs text-tertiary">
          Searching {garments.length} of {total} loaded. Search and the category list cover only the
          garments loaded so far; load more to include the rest.
        </p>
      ) : null}
      {garments.length === 0 ? (
        <Callout tone="neutral" title="No garments yet">
          This member has not added anything to their wardrobe yet.
        </Callout>
      ) : shown.length === 0 ? (
        <Callout tone="neutral" title="No garments match">
          {partial
            ? 'Clear the search, pick another category, or load more garments.'
            : 'Clear the search or pick another category.'}
        </Callout>
      ) : (
        <ul aria-label="Member wardrobe" className={GARMENT_GRID_CLASS}>
          {shown.map((garment) => (
            <GarmentTile key={garment.id} garment={garment} onOpen={() => setOpen(garment)} />
          ))}
        </ul>
      )}
      <LoadMore
        shown={garments.length}
        total={total}
        noun="garments"
        onLoadMore={onLoadMore}
        loading={loadingMore}
      />
      {open ? <GarmentDialog garment={open} onClose={() => setOpen(undefined)} /> : null}
    </Section>
  );
}

/** "N of total" plus a button that reads the next page, while more remain. */
function LoadMore({
  shown,
  total,
  noun,
  onLoadMore,
  loading,
  action,
}: {
  shown: number;
  total: number;
  noun: string;
  onLoadMore: (() => void) | undefined;
  loading: boolean;
  action?: string;
}) {
  if (shown >= total || !onLoadMore) return null;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <span className="text-sm text-tertiary">
        Showing {shown} of {total} {noun}.
      </span>
      <Button size="sm" color="secondary" isLoading={loading} onClick={onLoadMore}>
        {action ?? `Load more ${noun}`}
      </Button>
    </div>
  );
}

/* ------------------------------------------------------------------- outfits */

function OutfitPicture({ url, alt }: { url: string | null | undefined; alt: string }) {
  const [failed, setFailed] = useState(false);
  if (!url || failed) {
    return (
      <div
        role="img"
        aria-label={`${alt}: no picture yet`}
        className="flex aspect-[4/5] w-full items-center justify-center rounded-lg border border-secondary bg-secondary p-4 text-center text-xs text-tertiary"
      >
        No picture yet
      </div>
    );
  }
  return (
    <img
      src={url}
      alt={alt}
      onError={() => setFailed(true)}
      className="aspect-[4/5] w-full rounded-lg border border-secondary bg-secondary object-cover"
    />
  );
}

function OutfitCard({ outfit }: { outfit: MemberOutfitSummary }) {
  const title =
    outfit.display_title?.trim() ||
    (outfit.occasion_tag ? `${humanize(outfit.occasion_tag)} look` : 'Untitled look');
  return (
    <li className="flex min-w-0 flex-col gap-2 rounded-xl border border-secondary bg-primary p-3">
      <OutfitPicture url={outfit.render_image_url} alt={title} />
      <span className="truncate text-sm font-medium text-primary" title={title}>
        {title}
      </span>
      <span className="text-xs text-tertiary">
        {outfitSource(outfit.source)} · {relativeTime(outfit.created_at)}
      </span>
    </li>
  );
}

function Outfits({
  outfits,
  outfitsTotal,
  onLoadMore,
  loadingMore,
}: {
  outfits: MemberOutfitSummary[];
  outfitsTotal: number;
  onLoadMore: (() => void) | undefined;
  loadingMore: boolean;
}) {
  const grid = 'grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5';
  return (
    <Section
      title="Saved looks"
      description={`${outfits.length} of ${outfitsTotal}, newest first.`}
    >
      {outfits.length ? (
        <ul aria-label="Saved looks" className={grid}>
          {outfits.map((outfit) => (
            <OutfitCard key={outfit.id} outfit={outfit} />
          ))}
        </ul>
      ) : (
        <Callout tone="neutral" title="No saved looks yet" />
      )}
      <LoadMore
        shown={outfits.length}
        total={outfitsTotal}
        noun="looks"
        onLoadMore={onLoadMore}
        loading={loadingMore}
      />
    </Section>
  );
}

/* --------------------------------------------------------------- daily looks */

export const DAILY_LOOKS_PAGE = 10;

type DailyLooksState = {
  loading: boolean;
  /** Newest first. */
  looks: QueuedOutfit[];
  total: number;
  /** Queue offset of the oldest look loaded; older looks sit before it. */
  oldestOffset: number;
  error?: string;
};

/**
 * This member's day-ahead looks in every review status. The queue lists a
 * member's slots oldest first, so the newest page is read from the end of
 * the server total and older pages are read backwards from there.
 */
function useDailyLooks(review: ReviewService, memberId: string) {
  const [state, setState] = useState<DailyLooksState>({
    loading: true,
    looks: [],
    total: 0,
    oldestOffset: 0,
  });
  const read = useCallback(
    async (offset: number, limit: number) => {
      const page = await review.loadQueue({ reviewStatus: '', memberId, offset, limit });
      return { ...page, outfits: page.outfits.filter((look) => look.memberId === memberId) };
    },
    [memberId, review],
  );

  useEffect(() => {
    let current = true;
    setState({ loading: true, looks: [], total: 0, oldestOffset: 0 });
    void (async () => {
      const first = await read(0, DAILY_LOOKS_PAGE);
      const start = Math.max(0, first.total - DAILY_LOOKS_PAGE);
      const newest = start === 0 ? first : await read(start, DAILY_LOOKS_PAGE);
      return { looks: [...newest.outfits].reverse(), total: newest.total, oldestOffset: start };
    })()
      .then((page) => {
        if (current) setState({ loading: false, ...page });
      })
      .catch((cause: unknown) => {
        if (!current) return;
        setState({
          loading: false,
          looks: [],
          total: 0,
          oldestOffset: 0,
          error: cause instanceof Error ? cause.message : 'Daily looks could not be loaded.',
        });
      });
    return () => {
      current = false;
    };
  }, [read]);

  const loadOlder =
    state.oldestOffset > 0
      ? () => {
          if (state.loading) return;
          const start = Math.max(0, state.oldestOffset - DAILY_LOOKS_PAGE);
          setState((current) => ({ ...current, loading: true }));
          read(start, state.oldestOffset - start)
            .then((page) =>
              setState((current) => ({
                loading: false,
                looks: uniqueBySlot([...current.looks, ...[...page.outfits].reverse()]),
                total: page.total,
                oldestOffset: start,
              })),
            )
            .catch((cause: unknown) =>
              setState((current) => ({
                ...current,
                loading: false,
                error: cause instanceof Error ? cause.message : 'Older looks could not be loaded.',
              })),
            );
        }
      : undefined;

  return { state, loadOlder };
}

function uniqueBySlot(looks: QueuedOutfit[]): QueuedOutfit[] {
  const seen = new Set<string>();
  return looks.filter((look) => (seen.has(look.slotId) ? false : (seen.add(look.slotId), true)));
}

function DailyLook({ look, name }: { look: QueuedOutfit; name: string }) {
  const reviewer = reviewerLabel(look);
  return (
    <li className="flex flex-col gap-3 px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-semibold text-primary">{shortDate(look.forDate)}</span>
        <span className="text-xs font-medium text-secondary">
          {reviewStatusLabel(look.reviewStatus)}
        </span>
      </div>
      {look.garments.length ? (
        <GarmentThumbs garments={look.garments} label={`${name}'s look for ${look.forDate}`} />
      ) : (
        <span className="text-xs text-tertiary">No garments chosen yet</span>
      )}
      {reviewer ? (
        <span className="text-xs text-tertiary">
          Reviewed by {reviewer}
          {look.reviewedAt ? ` · ${relativeTime(look.reviewedAt)}` : ''}
        </span>
      ) : null}
      {look.stylistComment ? (
        <p className="text-sm text-secondary">“{look.stylistComment}”</p>
      ) : null}
    </li>
  );
}

function DailyLooks({
  review,
  memberId,
  name,
}: {
  review: ReviewService;
  memberId: string;
  name: string;
}) {
  const { state, loadOlder } = useDailyLooks(review, memberId);
  return (
    <Section
      title="Daily looks"
      description={
        state.total
          ? `${state.looks.length} of ${state.total}, newest first.`
          : 'The look made for each day, and how it was reviewed.'
      }
    >
      {state.error ? (
        <Callout tone="error" title="Daily looks could not be loaded">
          <FailureText raw={state.error} />
        </Callout>
      ) : null}
      {state.looks.length ? (
        <ol
          aria-label="Daily looks"
          className="flex max-w-3xl flex-col divide-y divide-secondary rounded-xl border border-secondary bg-primary"
        >
          {state.looks.map((look) => (
            <DailyLook key={look.slotId} look={look} name={name} />
          ))}
        </ol>
      ) : state.loading ? (
        <p className="text-sm text-tertiary">Loading daily looks…</p>
      ) : !state.error ? (
        <Callout tone="neutral" title="No daily looks yet" />
      ) : null}
      <LoadMore
        shown={state.looks.length}
        total={state.total}
        noun="looks"
        onLoadMore={loadOlder}
        loading={state.loading && state.looks.length > 0}
        action="Load older looks"
      />
    </Section>
  );
}

/* ------------------------------------------------------------------- profile */

function Profile({ detail }: { detail: MemberDetail }) {
  const updated = profileUpdated(detail);
  return (
    <div className="flex flex-col gap-4">
      {updated ? <p className="text-sm text-tertiary">{updated}</p> : null}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        {profileGroups(detail).map((group) => (
          <ProfileGroup key={group.title} {...group} />
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ activity */

function Activity({ detail }: { detail: MemberDetail }) {
  const activity = detail.recent_activity ?? [];
  return (
    <div className="flex flex-col gap-8">
      <Section
        title="Recent activity"
        description="Latest 25: outfit verdicts and orders, newest first."
      >
        {activity.length ? (
          <ol
            aria-label="Latest 25 activity"
            className="flex max-w-3xl flex-col divide-y divide-secondary rounded-xl border border-secondary bg-primary"
          >
            {activity.map((item) => (
              <li key={item.id} className="flex items-center justify-between gap-3 px-4 py-3">
                <span className="text-sm text-primary">{item.label}</span>
                <span className="shrink-0 text-xs text-tertiary">
                  {relativeTime(item.occurred_at)}
                </span>
              </li>
            ))}
          </ol>
        ) : (
          <Callout tone="neutral" title="No activity recorded yet" />
        )}
      </Section>
    </div>
  );
}

/* ---------------------------------------------------------------------- page */

export function MemberPage({
  members = memberService,
  review = reviewService,
}: {
  members?: MemberService;
  review?: ReviewService;
}) {
  const { clientId: memberId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = resolveTab(params.get('tab'));
  const [resource, setResource] = useState<{
    loading: boolean;
    data?: MemberDetail;
    error?: string;
  }>({ loading: true });
  const [more, setMore] = useState<MorePages>(NO_MORE);
  const [loadingMore, setLoadingMore] = useState<PagedList>();
  const [tomorrow, setTomorrow] = useState<QueuedOutfit | null>();
  const [actionError, setActionError] = useState<string>();

  const loadDetail = useCallback(async () => {
    if (!memberId) {
      setResource({ loading: false, error: 'This member link is missing an identifier.' });
      return;
    }
    setResource((current) => ({ ...current, loading: true }));
    try {
      const data = await members.member(memberId);
      setResource({ loading: false, data });
      setMore(NO_MORE);
    } catch (cause) {
      setResource({
        loading: false,
        error:
          cause instanceof Error ? cause.message : "This member's details could not be loaded.",
      });
    }
  }, [memberId, members]);

  useEffect(() => void loadDetail(), [loadDetail]);

  const detail = resource.data;
  const name = detail ? memberName(detail) : 'Member';

  const email = detail ? memberEmail(detail) : undefined;

  // The next slot awaiting review for this member: the queue lists it first.
  useEffect(() => {
    if (!memberId) return;
    let current = true;
    setTomorrow(undefined);
    review
      .loadQueue({ reviewStatus: 'awaiting_review', memberId, limit: 1 })
      .then((queue) => {
        if (current) setTomorrow(queue.outfits.find((o) => o.memberId === memberId) ?? null);
      })
      .catch(() => {
        if (current) setTomorrow(null);
      });
    return () => {
      current = false;
    };
  }, [memberId, review]);

  const garments = useMemo(
    () => uniqueById([...(detail?.garments ?? []), ...more.garments]),
    [detail?.garments, more.garments],
  );
  const outfits = useMemo(
    () => uniqueById([...(detail?.outfits ?? []), ...more.outfits]),
    [detail?.outfits, more.outfits],
  );
  const loaded: Record<PagedList, { length: number }> = { garments, outfits };
  const totals: Record<PagedList, number> = {
    garments: Math.max(detail?.garments_total ?? 0, garments.length),
    outfits: Math.max(detail?.outfits_total ?? 0, outfits.length),
  };
  const garmentTotal = totals.garments;

  // The member read pages every list by one shared offset, so the next page of
  // a list is a read at that list's loaded length, keeping only that list.
  const loadMoreOf = (list: PagedList) =>
    loaded[list].length < totals[list]
      ? () => {
          setLoadingMore(list);
          void members
            .member(memberId, loaded[list].length)
            .then((page) =>
              setMore((current) => ({
                ...current,
                [list]: [...current[list], ...(page[list] ?? [])],
              })),
            )
            .catch((cause: unknown) =>
              setActionError(cause instanceof Error ? cause.message : 'More could not be loaded.'),
            )
            .finally(() => setLoadingMore(undefined));
        }
      : undefined;

  const setTab = (next: TabId) => {
    const updated = new URLSearchParams(params);
    updated.set('tab', next);
    setParams(updated, { replace: true });
  };

  const onboardingStatus = textOf(detail?.onboarding_session, 'status');
  const onboarding = Boolean(onboardingStatus && onboardingStatus !== 'completed');
  const attentionCount = tomorrow ? 1 : 0;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <Button size="sm" color="link-gray" iconLeading={ArrowLeft} href="/">
          Back to my members
        </Button>
      </div>
      <ResourceState
        loading={resource.loading && !detail}
        loadingLabel="this member"
        error={resource.error}
        onRetry={() => void loadDetail()}
      >
        {detail ? (
          <>
            <header className="flex flex-col gap-4 border-b border-secondary pb-5">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="flex min-w-0 items-center gap-4">
                  <MemberAvatar name={name} src={tomorrow?.context.avatarUrl} size="lg" />
                  <div className="flex min-w-0 flex-col gap-1">
                    <h1 className="truncate text-xl font-semibold text-primary">{name}</h1>
                    {email ? <p className="truncate text-sm text-secondary">{email}</p> : null}
                    <p className="text-sm text-tertiary">Assigned to you</p>
                  </div>
                </div>
                <Button
                  size="sm"
                  color="secondary"
                  iconLeading={RefreshCw01}
                  isLoading={resource.loading}
                  onClick={() => void loadDetail()}
                >
                  Refresh
                </Button>
              </div>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                <MetaItem label="Account" value={accountStatus(textOf(detail.user, 'status'))} />
                <MetaItem
                  label="Onboarding"
                  value={humanize(textOf(detail.onboarding_session, 'status')) || NOT_PROVIDED}
                />
                <MetaItem label="Wardrobe" value={`${garmentTotal} garments`} />
              </div>
            </header>

            {actionError ? (
              <Callout tone="error" title="That did not work">
                <FailureText raw={actionError} />
              </Callout>
            ) : null}

            <Tabs
              className="gap-5"
              selectedKey={tab}
              onSelectionChange={(key) => setTab(resolveTab(String(key)))}
            >
              <Tabs.List
                type="underline"
                size="md"
                aria-label="Member sections"
                className="overflow-x-auto border-b border-secondary"
              >
                {MEMBER_TABS.map((item) => (
                  <Tabs.Item
                    key={item.id}
                    id={item.id}
                    label={item.label}
                    {...(item.id === 'attention' && attentionCount
                      ? { badge: attentionCount }
                      : {})}
                  />
                ))}
              </Tabs.List>

              <Tabs.Panel id="attention">
                <div className="flex flex-col gap-8">
                  <Section title="Tomorrow's outfit">
                    <TomorrowOutfit outfit={tomorrow} name={name} />
                  </Section>
                  {onboarding ? (
                    <Section title="Onboarding">
                      <p className="text-sm text-secondary">
                        Onboarding in progress · {humanize(onboardingStatus)}
                        {typeof detail.onboarding_session?.current_step === 'string'
                          ? `, at ${humanize(detail.onboarding_session.current_step)}`
                          : ''}
                      </p>
                    </Section>
                  ) : null}
                </div>
              </Tabs.Panel>

              <Tabs.Panel id="wardrobe">
                <Wardrobe
                  garments={garments}
                  total={garmentTotal}
                  onLoadMore={loadMoreOf('garments')}
                  loadingMore={loadingMore === 'garments'}
                />
              </Tabs.Panel>

              <Tabs.Panel id="outfits">
                <div className="flex flex-col gap-8">
                  <DailyLooks review={review} memberId={memberId} name={name} />
                  <Outfits
                    outfits={outfits}
                    outfitsTotal={totals.outfits}
                    onLoadMore={loadMoreOf('outfits')}
                    loadingMore={loadingMore === 'outfits'}
                  />
                </div>
              </Tabs.Panel>

              <Tabs.Panel id="profile">
                <Profile detail={detail} />
              </Tabs.Panel>

              <Tabs.Panel id="activity">
                <Activity detail={detail} />
              </Tabs.Panel>
            </Tabs>
          </>
        ) : null}
      </ResourceState>
    </div>
  );
}
